import { useCallback, useEffect, useState } from "react";
import { Library, Eye, Pencil } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

export default function AdminQuestions() {
  const { user } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [editTarget, setEditTarget] = useState(null);
  const [previewTarget, setPreviewTarget] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/questions");
      setQuestions(data.questions);
    } catch (err) {
      toast.error("Errore caricamento", { description: formatApiError(err) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (q) => {
    setEditTarget(q);
    setForm({
      question_text: q.question_text, help_text: q.help_text || "",
      why_text: q.why_text || "", tooltip: q.tooltip || "",
      order_index: q.order_index, active: q.active,
    });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/questions/${editTarget.question_code}`, form);
      toast.success("Domanda aggiornata", { description: editTarget.question_code });
      setEditTarget(null);
      load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  if (!user?.is_superadmin) {
    return (
      <div className="max-w-xl" data-testid="admin-questions-forbidden">
        <Card className="border-slate-200"><CardContent className="p-6 text-sm text-slate-600">
          Sezione riservata al Super Admin della piattaforma.
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-questions-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <Library className="w-7 h-7 text-green-700" aria-hidden="true" />
          Question Library
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          {questions.length} domande configurate. Ogni domanda è mappata sui datapoint VSME senza hardcoding nel frontend.
        </p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <Table data-testid="questions-table">
            <TableHeader>
              <TableRow>
                <TableHead>Codice</TableHead><TableHead>Domanda</TableHead><TableHead>Tipo</TableHead>
                <TableHead>Modulo</TableHead><TableHead>Mapping VSME</TableHead><TableHead>Regole</TableHead>
                <TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {questions.map((q) => (
                <TableRow key={q.question_code} data-testid={`question-row-${q.question_code}`}>
                  <TableCell className="font-mono text-xs">{q.question_code}</TableCell>
                  <TableCell className="max-w-xs text-sm">{q.question_text}</TableCell>
                  <TableCell><Badge variant="outline" className="bg-slate-50 text-[11px]">{q.question_type}</Badge></TableCell>
                  <TableCell className="text-xs">{q.module}</TableCell>
                  <TableCell className="font-mono text-[11px] max-w-[220px]">
                    {(q.mappings || []).map((m) => (
                      <span key={m.id} className="block text-green-800">{m.datapoint_code} <span className="text-slate-400">({m.mapping_type})</span></span>
                    ))}
                    {(q.mappings || []).length === 0 && <span className="text-slate-400">—</span>}
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {(q.rules || []).map((r) => (
                      <span key={r.id} className="block">se {r.condition_definition?.question_code} {r.condition_definition?.operator} {JSON.stringify(r.condition_definition?.value)}</span>
                    ))}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={q.active ? "bg-green-50 text-green-800 border-green-300" : "bg-slate-100 text-slate-500"}>
                      {q.active ? "Attiva" : "Disattivata"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setPreviewTarget(q)} data-testid={`preview-${q.question_code}`}>
                        <Eye className="w-4 h-4" aria-hidden="true" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(q)} data-testid={`edit-${q.question_code}`}>
                        <Pencil className="w-4 h-4" aria-hidden="true" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!editTarget} onOpenChange={() => setEditTarget(null)}>
        <DialogContent className="max-w-xl" data-testid="question-edit-dialog">
          <DialogHeader>
            <DialogTitle>Modifica domanda</DialogTitle>
            <DialogDescription className="font-mono text-xs">{editTarget?.question_code} — le versioni precedenti del testo vengono conservate.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="qe-text">Testo domanda</Label>
              <Textarea id="qe-text" rows={2} value={form.question_text || ""}
                onChange={(e) => setForm({ ...form, question_text: e.target.value })} data-testid="question-edit-text" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="qe-help">Testo di aiuto (?)</Label>
              <Textarea id="qe-help" rows={2} value={form.help_text || ""}
                onChange={(e) => setForm({ ...form, help_text: e.target.value })} data-testid="question-edit-help" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="qe-why">"Perché me lo chiedi?"</Label>
              <Textarea id="qe-why" rows={2} value={form.why_text || ""}
                onChange={(e) => setForm({ ...form, why_text: e.target.value })} data-testid="question-edit-why" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="qe-tooltip">Tooltip tecnico</Label>
              <Input id="qe-tooltip" value={form.tooltip || ""}
                onChange={(e) => setForm({ ...form, tooltip: e.target.value })} data-testid="question-edit-tooltip" />
            </div>
            <div className="flex items-center gap-3">
              <Switch id="qe-active" checked={!!form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="question-edit-active" />
              <Label htmlFor="qe-active">Domanda attiva</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditTarget(null)} data-testid="question-edit-cancel">Annulla</Button>
              <Button type="submit" className="bg-green-700 hover:bg-green-800" data-testid="question-edit-save">Salva</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewTarget} onOpenChange={() => setPreviewTarget(null)}>
        <DialogContent data-testid="question-preview-dialog">
          <DialogHeader>
            <DialogTitle>Anteprima utente</DialogTitle>
            <DialogDescription>Come appare la domanda all'imprenditore (senza salvare dati).</DialogDescription>
          </DialogHeader>
          {previewTarget && (
            <div className="border border-slate-200 rounded-lg p-5 bg-slate-50 space-y-3">
              <p className="text-lg font-semibold text-slate-900">{previewTarget.question_text}</p>
              {previewTarget.question_type === "YES_NO_UNKNOWN" && (
                <div className="flex gap-2">
                  {["SÌ", "NO", "NON SO"].map((l) => (
                    <span key={l} className="px-5 py-2.5 rounded-lg border-2 border-slate-300 bg-white text-sm font-bold text-slate-600">{l}</span>
                  ))}
                </div>
              )}
              {previewTarget.question_type === "SINGLE_SELECT" && (
                <div className="space-y-1.5">
                  {(previewTarget.options || []).map((o) => (
                    <p key={o.value} className="px-4 py-2.5 rounded-lg border-2 border-slate-300 bg-white text-sm text-slate-600">{o.label}</p>
                  ))}
                </div>
              )}
              {!["YES_NO_UNKNOWN", "SINGLE_SELECT", "YES_NO"].includes(previewTarget.question_type) && (
                <p className="px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-sm text-slate-400">
                  {previewTarget.placeholder || `Campo di tipo ${previewTarget.question_type}`}{previewTarget.unit ? ` (${previewTarget.unit})` : ""}
                </p>
              )}
              {previewTarget.help_text && (
                <p className="text-xs text-slate-600">(?) {previewTarget.help_text}</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
